/**
 * Currency formatting for every console / admin surface (DEC-042): amounts come from the API as
 * decimal strings in major units of an ISO-4217 currency and are shown with
 * `Intl.NumberFormat(locale, { style: "currency", currency })`. Never hard-code a symbol.
 */

/** Region used with each app language per currency (PHP amounts read best in the PH locale). */
const CURRENCY_REGIONS: Readonly<Record<string, string>> = { PHP: "PH", USD: "US" };
const FALLBACK_REGION = "US";
const FIL_REGION = "PH";

/** App language (`en` | `fil`, or a full BCP-47 tag) + currency → BCP-47 locale for Intl. */
export function moneyLocale(language: string, currency: string): string {
  if (language.includes("-")) return language;
  if (language === "fil") return `fil-${FIL_REGION}`;
  return `${language}-${CURRENCY_REGIONS[currency.toUpperCase()] ?? FALLBACK_REGION}`;
}

export interface MoneyOptions {
  /** Keep minor units even when zero (invoices, receipts). Default: only when non-zero. */
  readonly exact?: boolean;
  /** Compact notation (`₱1.8M`) for KPI cards. */
  readonly compact?: boolean;
}

function toNumber(amount: string | number | null | undefined): number | null {
  if (amount === null || amount === undefined || amount === "") return null;
  const value = typeof amount === "number" ? amount : Number(amount);
  return Number.isFinite(value) ? value : null;
}

/** `formatMoney("7500.00", "PHP", "en")` → `₱7,500`; `("146.67", "USD", "en")` → `$146.67`. */
export function formatMoney(
  amount: string | number | null | undefined,
  currency: string,
  language: string,
  options: MoneyOptions = {},
): string {
  const value = toNumber(amount) ?? 0;
  const code = currency.toUpperCase();
  const fraction = new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  const showMinor = options.exact === true || !Number.isInteger(value);
  return new Intl.NumberFormat(moneyLocale(language, code), {
    style: "currency",
    currency: code,
    notation: options.compact ? "compact" : "standard",
    minimumFractionDigits: options.compact ? 0 : showMinor ? fraction : 0,
    maximumFractionDigits: options.compact ? 1 : fraction,
  }).format(value);
}
